import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  notification,
  Radio,
  Select,
  Input,
  InputNumber,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  Badge,
  Switch,
  Popconfirm,
  Typography,
  Alert,
  Spin,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { t } from '../../i18n/i18n';
import { saveTemplateEmail } from '../../API/services/alkymersService';
import { setSubjectEmail, setTemplate } from '../../redux/actions/screeningActions';
import { fetchUniqueWorkspaceMembers } from '../../utils/workspaceMembersUtils';
import './EditEmailTemplate.scss';
import useCreateSearch from '../../hooks/useCreateSearch';
import ModalCustom from '../ModalCustom/ModalCustom';
import { TrackEventGTM } from '../../utils/filters/analyticsUtils';
import { Window } from '../../utils/window';
import SafeContainerTinyMCE from '../SafeContainer/SafeContainerTinyMCE';
import EmailTagsInput from './EmailTagsInput';
import useTranslations from '../../hooks/useTranslations';
import CountryFlag from '../CountryFlag/CountryFlag';
import { useLanguageTranslations } from '../../hooks/useLanguageTranslations';
import { EventsGTM } from '../../constants/events';
import {
  styleEmailPreviewListItems,
  translateTemplateHTML,
  translateTemplateLabel,
} from '../../utils/emailsUtils';

const defaultKeys = ['{{firstName}}', '{{lastName}}', '{{companyName}}', '{{email}}'];
const EditCustomEmailTemplate = ({
  goBack,
  handleSendRequest,
  viewInfo = true,
  emailDashboard = false,
  emails = [],
  customTemplate = null,
  newCustomEmail = false,
}) => {
  const { template, newSearch, subjectEmail } = useSelector((state) => state.screenings);
  const defaultCustomEmail = useMemo(() => customTemplate || template, [customTemplate, template]);
  const { user, currentWorkspace, currentRole, email } = useSelector((state) => state.auth);
  const { key } = useTranslations();
  const { languagesList } = useLanguageTranslations();
  const [selectedLang, setSelectedLang] = useState(key);
  const [disabledTitleBtn, setDisabledTitleBtn] = useState(true);
  const [newTitle, setNewTitle] = useState(
    customTemplate?.translations?.[selectedLang]?.[customTemplate?.title] ??
      t(customTemplate?.title)
  );
  const [oldTitle, setOldTitle] = useState('');
  const [selectLanguages, setSelectLanguages] = useState(Object.keys(customTemplate?.translations));

  const { step } = useCreateSearch();
  const dispatch = useDispatch();
  const history = useHistory();
  const [value, setValue] = useState('');
  const translateHTML = (html, lang = 'es') => {
    return translateTemplateHTML(html, lang, defaultCustomEmail);
  };
  const translateEmail = (LABEL, lang = 'es') => {
    return translateTemplateLabel(LABEL, lang, defaultCustomEmail);
  };

  const [htmlTranslate, setHtmlTranslate] = useState({
    es: {
      HTML: translateHTML(customTemplate?.html, 'es'),
      SUBJECT: translateEmail(customTemplate?.subject, 'es'),
    },
    en: {
      HTML: translateHTML(customTemplate?.html, 'en'),
      SUBJECT: translateEmail(customTemplate?.subject, 'en'),
    },
    pt: {
      HTML: translateHTML(customTemplate?.html, 'pt'),
      SUBJECT: translateEmail(customTemplate?.subject, 'pt'),
    },
  });
  const [subject, setSubject] = useState(subjectEmail.subjectEmail);
  const [buttonText, setButtonText] = useState(t('GO_TO_PLATFORM'));
  const regex = new RegExp(/\{(.+?)\}/g);
  const [result, setResult] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [emailsToSend, setEmailsToSend] = useState(emails);
  const [ccEmailsToSend, setCcEmailsToSend] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [reminderDays, setReminderDays] = useState(customTemplate?.reminderDays || [-1, 3, 7]);
  const [reminderEnabled, setReminderEnabled] = useState(!!customTemplate?.reminderDays);
  const isTestReminder = customTemplate?.eventType === 'TEST_REMINDER';

  const { Option } = Select;

  const mapObj = {
    '{btn-url}': `<a class="custom-button">Ir a campus<span>→</span></a>`,
    '{{firstName}}': t('EMAIL_TO_SEND_FIRSTNAME'),
    '{{companyName}}': t('ALKEMY'),
    '{{lastName}}': t('EMAIL_TO_SEND_LASTNAME'),
    '{{score}}': t('EMAIL_TO_SEND_SCORE'),
    '{{email}}': 'alkemy@test.com',
    ...(customTemplate
      ? Object.fromEntries(
          Object.entries(customTemplate?.config?.variables ?? {})?.map(([key, value]) => [
            `{{${key}}}`,
            value,
          ])
        )
      : {}),
  };
  useEffect(() => {
    const url = history.location.pathname;
    if (Window() && url !== '/create-template') {
      window.onpopstate = () => {
        history.goForward();
      };
    }
    return () => {
      if (Window()) {
        window.onpopstate = () => {};
      }
    };
  }, []);

  useEffect(() => {
    const fetchMembers = async () => {
      if (currentWorkspace) {
        setLoadingMembers(true);
        try {
          const uniqueEmails = await fetchUniqueWorkspaceMembers(currentWorkspace);
          setWorkspaceMembers(uniqueEmails);
        } catch (error) {
          console.error('Error fetching workspace members:', error);
        } finally {
          setLoadingMembers(false);
        }
      }
    };
    fetchMembers();
  }, [currentWorkspace]);

  useEffect(() => {
    const html = translateHTML(defaultCustomEmail?.html, selectedLang);
    const newsubject = translateEmail(defaultCustomEmail?.subject, selectedLang);
    setSubject(newsubject);
    setValue(html);

    dispatch(
      setSubjectEmail({
        subjectEmail: defaultCustomEmail?.translated?.subject
          ? defaultCustomEmail?.translated?.subject
          : '',
      })
    );
  }, []);

  useEffect(() => {
    const allKeys = Array.from(
      new Set([
        ...defaultKeys,
        ...Object.keys(customTemplate?.config?.variables ?? {})?.map((key) => `{{${key}}}`),
      ])
    );
    const regexPattern = allKeys.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const dynamicRegex = new RegExp(regexPattern, 'g');

    const previewDiv = document.getElementById('divCustomPreview');
    if (previewDiv) {
      const baseHtml = value;
      const htmlContent = translateTemplateHTML(
        baseHtml?.replace(dynamicRegex, (matched) => mapObj[matched]),
        selectedLang,
        defaultCustomEmail
      );
      previewDiv.innerHTML = htmlContent;
      styleEmailPreviewListItems(previewDiv);
    }

    if (
      value.match(regex) !== null &&
      !!value.match(regex).length &&
      JSON.stringify(value.match(regex)) !== JSON.stringify(result)
    )
      setResult(value.match(regex));
  }, [value]);

  const [previousLang, setPreviousLang] = useState(key);

  useEffect(() => {
    // Guardar el contenido del idioma anterior antes de cambiar
    if (previousLang !== selectedLang && value) {
      setHtmlTranslate((prev) => ({
        ...prev,
        [previousLang]: {
          ...prev[previousLang],
          HTML: value,
          SUBJECT: subject,
        },
      }));
    }

    const html =
      htmlTranslate[selectedLang]?.HTML ||
      translateTemplateHTML(defaultCustomEmail?.html, selectedLang, defaultCustomEmail);
    const newsubject =
      htmlTranslate[selectedLang]?.SUBJECT ||
      translateTemplateLabel(defaultCustomEmail?.subject, selectedLang, defaultCustomEmail);

    setValue(html);
    setSubject(newsubject);
    setPreviousLang(selectedLang);
  }, [selectedLang]);

  const onSelectLanguages = (value = []) => {
    if (!value.length) {
      notification.error({
        message: t('ERROR_NO_LANGUAGES_SELECTED'),
        description: t('PLEASE_SELECT_AT_LEAST_ONE_LANGUAGE'),
        duration: 4,
      });
      return;
    }
    const htmlDefault =
      htmlTranslate[selectedLang]?.HTML || translateHTML(defaultCustomEmail?.html, selectedLang);
    const newsubjectDefault =
      htmlTranslate[selectedLang]?.SUBJECT ||
      translateEmail(defaultCustomEmail?.subject, selectedLang);
    const newHtmlTranslate = htmlTranslate;
    value?.map((value) => {
      let html = htmlDefault;
      let newSubject = newsubjectDefault;
      if (htmlTranslate[value]?.HTML) {
        html = htmlTranslate[value]?.HTML;
      }
      if (htmlTranslate[value]?.SUBJECT) {
        newSubject = htmlTranslate[value]?.SUBJECT;
      }
      newHtmlTranslate[value].SUBJECT = newSubject;
      newHtmlTranslate[value].HTML = html;
    });
    setHtmlTranslate(newHtmlTranslate);
    setSelectLanguages(value);
    if (!value.includes(selectedLang)) {
      setSelectedLang(value[0]);
    }
  };

  useEffect(() => {
    const allKeys = Array.from(
      new Set([
        ...defaultKeys,
        ...Object.keys(customTemplate?.config?.variables ?? {})?.map((key) => `{{${key}}}`),
      ])
    );

    if (result.length && !result?.every((string) => allKeys.includes(string))) {
      setError(true);
    } else {
      setError(false);
    }
  }, [result]);

  const handleCustomEmail = async () => {
    TrackEventGTM(EventsGTM.SEARCH_SEND_EMAIL, {
      userEmail: email,
      event_action: 'send',
      event_category: 'Send custom email',
    });
    const htmlRequest = {};
    selectLanguages.forEach((lang) => {
      htmlRequest[lang] = htmlTranslate[lang];
    });
    if (subject !== '') {
      if (!htmlRequest || Object.keys(htmlRequest).length === 0) {
        notification.error({
          message: t('ERROR_NO_LANGUAGES_SELECTED'),
          description: t('PLEASE_SELECT_AT_LEAST_ONE_LANGUAGE'),
          duration: 4,
        });
        return;
      }
      setShowModal(true);
    } else {
      notification.error({
        message: t('SUBJECT_EMAIL_ERROR'),
      });
    }
  };
  const handleSaveCustomEmail = async () => {
    TrackEventGTM(EventsGTM.SEARCH_SEND_EMAIL, {
      userEmail: email,
      event_action: 'send',
      event_category: 'Send and save custom email',
    });
    const htmlRequest = {};
    selectLanguages.forEach((lang) => {
      htmlRequest[lang] = htmlTranslate[lang];
    });
    if (subject !== '') {
      try {
        const res = await saveTemplateEmail(
          value,
          subject,
          '',
          buttonText,
          {
            ...customTemplate,
            title: newTitle || customTemplate?.title,
            reminderDays: isTestReminder && reminderEnabled ? reminderDays : undefined,
          },
          currentWorkspace,
          htmlRequest,
          newCustomEmail
        );

        if (res && res.id) {
          dispatch(setTemplate({ template: res.html, id: res.id }));
          dispatch(setSubjectEmail({ subjectEmail: subject }));
          notification.success({
            message: newCustomEmail ? t('TEMPLATE_CREATED_SUCCESSFULLY') : t('TEMPLATE_SAVED'),
            duration: 3,
          });
          if (goBack) {
            goBack(true);
          }
        } else {
          notification.error({
            message: t('ERROR_SAVING_TEMPLATE'),
            duration: 4,
          });
        }
      } catch (error) {
        console.error('Error saving template:', error);
        notification.error({
          message: t('ERROR_SAVING_TEMPLATE'),
          description: error?.response?.data?.message || error?.message,
          duration: 4,
        });
      }
    } else {
      notification.error({
        message: t('SUBJECT_EMAIL_ERROR'),
      });
    }
  };

  const handleGenericEmail = () => {
    TrackEventGTM(EventsGTM.SEARCH_SEND_EMAIL, {
      userEmail: email,
      event_action: 'send',
      event_category: 'Send custom email',
    });
    dispatch(setTemplate({ template: value }));
    if (subject !== '') {
      dispatch(setSubjectEmail({ subjectEmail: subject }));
      setShowModal(true);
    } else {
      notification.error({
        message: t('SUBJECT_EMAIL_ERROR'),
      });
    }
  };

  const handleSendEmail = async () => {
    TrackEventGTM(EventsGTM.SEARCH_SEND_EMAIL, {
      userEmail: email,
      event_action: 'send',
      event_category: 'Confirmation send custom email',
    });
    setShowModal(false);
    const htmlToSend = value;
    handleSendRequest(
      error,
      setLoading,
      emailsToSend,
      htmlToSend,
      subject,
      defaultCustomEmail?.id,
      ccEmailsToSend
    );
  };

  return (
    <div data-testid="edit-email-template-component" className="w-100">
      <ModalCustom
        title={t('CREATE_SEARCH_CLICK_SEND_SEARCH')}
        subtitle={t('CREATE_SEARCH_CHANGE_DATA_QUESTION')}
        show={showModal}
        setShow={setShowModal}
        onOk={handleSendEmail}
        onOkDisabled={loading}
        onCancel={() => setShowModal(false)}
        classModal="modal-wrap-add-row"
        onCancelText={t('CREATE_SEARCH_CANCEL_SEARCH')}
        onOkText={t('SEND_CUSTOM_EMAIL_BTN')}
        showCross={false}
        datatestidbuttonok="send-email-button"
      />
      <div className="custom-email-editor-container">
        {newSearch?.name && (
          <Alert
            message={`${t('CREATE_SEARCH_CAPSULE_TITLE')}: ${newSearch.name}`}
            type="info"
            className="mb-3"
            icon={<InfoCircleOutlined />}
          />
        )}
        {viewInfo && (
          <div className="d-flex justify-content-between align-items-start">
            {step <= 3 && (
              <span className="text-sm font-montserrat text-muted">
                {t('CREATE_SEARCH_PLACED_STEP').replace('{step}', step)}
              </span>
            )}
          </div>
        )}
        <Card className="mb-3">
          <Row gutter={8} align="middle">
            <Col flex="auto">
              <Typography.Text strong className="text-lg mr-2">
                {t('TITLE')}:
              </Typography.Text>
              {disabledTitleBtn ? (
                <Typography.Text className="text-lg">{newTitle}</Typography.Text>
              ) : (
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder={t('TITLE')}
                  data-testid="alkemy-tests-search-input"
                  className="d-inline-block"
                  style={{ width: '300px' }}
                />
              )}
            </Col>
            <Col flex="none">
              <Space>
                <Button
                  icon={disabledTitleBtn ? <EditOutlined /> : <CheckOutlined />}
                  onClick={() => {
                    if (disabledTitleBtn) {
                      setOldTitle(newTitle);
                    }
                    setDisabledTitleBtn(!disabledTitleBtn);
                  }}
                />
                {!disabledTitleBtn && (
                  <Button
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setNewTitle(oldTitle);
                      setDisabledTitleBtn(!disabledTitleBtn);
                    }}
                  />
                )}
              </Space>
            </Col>
          </Row>
        </Card>
        <Card className="mb-4 variables-info-card">
          <Typography.Title level={5}>{t('AVAILABLE_VARIABLES')}</Typography.Title>

          <Space wrap className="mb-2">
            {customTemplate?.sendType === 'MANUAL' && (
              <>
                <Tooltip title={t('VARIABLE_FIRSTNAME_DESC')}>
                  <Tag color="blue" className="cursor-pointer">
                    {'{{firstName}}'}
                  </Tag>
                </Tooltip>
                <Tooltip title={t('VARIABLE_LASTNAME_DESC')}>
                  <Tag color="blue" className="cursor-pointer">
                    {'{{lastName}}'}
                  </Tag>
                </Tooltip>
                <Tooltip title={t('VARIABLE_COMPANY_DESC')}>
                  <Tag color="blue" className="cursor-pointer">
                    {'{{companyName}}'}
                  </Tag>
                </Tooltip>
                <Tooltip title={t('VARIABLE_EMAIL_DESC')}>
                  <Tag color="blue" className="cursor-pointer">
                    {'{{email}}'}
                  </Tag>
                </Tooltip>
              </>
            )}

            {customTemplate?.config?.variables &&
              Object.entries(customTemplate.config.variables).map(([key, value]) => (
                <Tooltip key={key} title={value}>
                  <Tag color="green" className="cursor-pointer">
                    {`{{${key}}}`}
                  </Tag>
                </Tooltip>
              ))}
          </Space>

          <Typography.Text type="secondary" className="text-sm">
            {t('TEXT_CREATE_TEMPLATE')}
          </Typography.Text>
        </Card>

        {isTestReminder && (
          <Card className="mb-4 email-reminder-card">
            <Row align="middle" gutter={16}>
              <Col span={20}>
                <Typography.Title level={5} className="mb-0">
                  <ClockCircleOutlined className="mr-2" />
                  {t('REMINDER_DAYS_CONFIGURATION')}
                </Typography.Title>
              </Col>
              <Col span={4} className="text-right">
                <Switch checked={reminderEnabled} onChange={setReminderEnabled} />
              </Col>
            </Row>

            {reminderEnabled && (
              <>
                <Typography.Text type="secondary" className="d-block mt-3 mb-3">
                  {t('REMINDER_DAYS_DESCRIPTION')}
                </Typography.Text>

                <Space direction="vertical" className="w-100">
                  {reminderDays.map((day, index) => (
                    <Row key={index} gutter={16} align="middle">
                      <Col span={4}>
                        <InputNumber
                          value={day}
                          onChange={(value) => {
                            const newDays = [...reminderDays];
                            newDays[index] = value || 0;
                            setReminderDays(newDays);
                          }}
                          min={-30}
                          max={30}
                          className="w-100"
                        />
                      </Col>
                      <Col span={16}>
                        <Badge
                          status={day < 0 ? 'warning' : 'processing'}
                          text={
                            <Typography.Text>
                              {day < 0
                                ? `${Math.abs(day)} ${t('DAYS_BEFORE_END')}`
                                : `${day} ${t('DAYS_AFTER_INVITATION')}`}
                            </Typography.Text>
                          }
                        />
                      </Col>
                      <Col span={4} className="text-right">
                        <Popconfirm
                          title={t('CONFIRM_DELETE_REMINDER')}
                          onConfirm={() => {
                            const newDays = reminderDays.filter((_, i) => i !== index);
                            setReminderDays(newDays);
                          }}
                          okText={t('YES')}
                          cancelText={t('NO')}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    type="dashed"
                    onClick={() => setReminderDays([...reminderDays, 1])}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t('ADD_REMINDER_DAY')}
                  </Button>
                </Space>
              </>
            )}
          </Card>
        )}

        <Card className="mb-4">
          <Typography.Title level={5} className="mb-3">
            {t('EMAIL_DASHBOARD_SUBTITLE')}
          </Typography.Title>

          {emails?.length ? (
            <>
              <Row gutter={24} className="mb-2">
                <Col xs={24} md={12}>
                  <Typography.Text strong className="d-block mb-2">
                    {t('TO')}:
                  </Typography.Text>
                </Col>
                <Col xs={24} md={12}>
                  <Typography.Text strong className="d-block mb-2">
                    {t('CC_LABEL')}:
                  </Typography.Text>
                  <Typography.Text type="secondary" className="d-block text-sm">
                    {t('CC_DESCRIPTION')}
                  </Typography.Text>
                </Col>
              </Row>
              <Row gutter={24} className="mb-4">
                <Col xs={24} md={12}>
                  <EmailTagsInput setEmails={setEmailsToSend} emails={emailsToSend} />
                </Col>
                <Col xs={24} md={12}>
                  <Spin spinning={loadingMembers} size="small">
                    <EmailTagsInput
                      setEmails={setCcEmailsToSend}
                      emails={ccEmailsToSend}
                      suggestions={workspaceMembers}
                      placeholder={t('CC_PLACEHOLDER')}
                    />
                  </Spin>
                </Col>
              </Row>
            </>
          ) : null}

          <Select
            mode="multiple"
            style={{
              width: '100%',
              fontFamily: 'Montserrat',
            }}
            className="font-montserrat mb-4"
            placeholder={t('CREATE_TEST_GROUP_LANGUAGES_PLACEHOLDER')}
            onChange={onSelectLanguages}
            value={selectLanguages}
            popupClassName="font-montserrat"
            dropdownStyle={{ fontFamily: 'Montserrat' }}
          >
            {languagesList.map((x) => (
              <Option
                key={x.value}
                value={x.value}
                style={{
                  fontFamily: 'Montserrat',
                }}
              >
                <CountryFlag countryIso={x.iso} size="sm" className="mr-2" />
                {t(x.label)}
              </Option>
            ))}
          </Select>

          {customTemplate && (
            <div className="mb-4">
              <Radio.Group
                value={selectedLang ?? key}
                onChange={(e) => setSelectedLang(e.target.value)}
              >
                {selectLanguages?.map((lang) => (
                  <Radio.Button key={lang} value={lang}>
                    <CountryFlag
                      countryIso={languagesList?.find((item) => item?.value === lang)?.iso}
                      size="sm"
                    />
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}
          <Input
            data-testid="subject-input"
            value={subject}
            placeholder={t('EMAIL_TEMPLATE_PLACEHOLDER_SUBJECT')}
            size="large"
            className="mb-3"
            onChange={(e) => {
              setSubject(e.target.value);
              setHtmlTranslate({
                ...htmlTranslate,
                [selectedLang]: { ...htmlTranslate[selectedLang], SUBJECT: e.target.value },
              });
            }}
          />
          {typeof window !== 'undefined' && (
            <SafeContainerTinyMCE
              key={selectedLang}
              content={value}
              handleChange={(val) => {
                setValue(val);
                setHtmlTranslate({
                  ...htmlTranslate,
                  [selectedLang]: { ...htmlTranslate[selectedLang], HTML: val },
                });
              }}
              placeholder={t('EMAIL_TEMPLATE_PLACEHOLDER_CONTENT')}
              height={400}
              language={key}
            />
          )}
        </Card>
        <Row justify="space-between" className="mt-4">
          <Col>
            <Button
              data-testid="go-back-button"
              size="large"
              onClick={() => {
                if (goBack) {
                  goBack();
                } else {
                  history.goBack();
                }
              }}
            >
              {t('CREATE_SEARCH_STAGE_BACK')}
            </Button>
          </Col>
          <Col>
            <Space>
              {customTemplate && (
                <Button
                  disabled={loading}
                  loading={loading}
                  data-testid="send-evaluation-button"
                  type="primary"
                  size="large"
                  className="custom-email-save-btn"
                  onClick={handleSaveCustomEmail}
                >
                  {newCustomEmail ? t('CREATE') : t('SAVE')}
                </Button>
              )}
              {customTemplate?.sendType === 'MANUAL' && (
                <Button
                  disabled={loading}
                  loading={loading}
                  data-testid="send-evaluation-button"
                  type="primary"
                  size="large"
                  onClick={emailDashboard ? handleGenericEmail : handleCustomEmail}
                >
                  {t('SEND')}
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EditCustomEmailTemplate;
